import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, GripVertical, Plus, Search, X } from "lucide-react";
import {
  ClientRecord, Item, OCCASIONS, OUTFIT_ROLES, Occasion, OutfitRole,
  clientsApi, fileUrl, inferOutfitRole, itemsApi, outfitsApi,
} from "@/lib/catalog-api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui-shim";
import { toast } from "sonner";

interface Pick {
  item: Item;
  role: OutfitRole;
}

export default function OutfitNew() {
  const navigate = useNavigate();
  const [allItems, setAllItems] = useState<Item[]>([]);
  const [clients, setClients] = useState<ClientRecord[]>([]);
  const [search, setSearch] = useState("");
  const [picks, setPicks] = useState<Pick[]>([]);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [clientId, setClientId] = useState("");
  const [occasion, setOccasion] = useState<Occasion | "">("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    itemsApi.list({ page_size: 100 }).then((r) => setAllItems(r.items)).catch(() => setAllItems([]));
    clientsApi.list().then(setClients).catch(() => setClients([]));
  }, []);

  const pickedIds = useMemo(() => new Set(picks.map((p) => p.item._id)), [picks]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = allItems.filter((it) => !pickedIds.has(it._id));
    if (clientId) list = list.filter((it) => it.client_id === clientId);
    if (q) {
      list = list.filter((it) =>
        it.name.toLowerCase().includes(q) ||
        (it.brand || "").toLowerCase().includes(q) ||
        (it.tags || []).some((t) => t.toLowerCase().includes(q))
      );
    }
    return list;
  }, [allItems, search, clientId, pickedIds]);

  function add(it: Item) {
    setPicks((p) => [...p, { item: it, role: inferOutfitRole(it.category) }]);
  }
  function remove(id: string) {
    setPicks((p) => p.filter((i) => i.item._id !== id));
  }
  function setRole(id: string, role: OutfitRole) {
    setPicks((p) => p.map((pk) => pk.item._id === id ? { ...pk, role } : pk));
  }

  // ----- HTML5 native drag-and-drop reordering inside picks -----
  const dragIndex = useRef<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);
  function onDragStart(i: number) { dragIndex.current = i; }
  function onDragOver(e: React.DragEvent, i: number) {
    e.preventDefault();
    if (dragIndex.current !== null && dragIndex.current !== i) setDragOver(i);
  }
  function onDrop(i: number) {
    const from = dragIndex.current;
    dragIndex.current = null;
    setDragOver(null);
    if (from === null || from === i) return;
    setPicks((arr) => {
      const next = [...arr];
      const [moved] = next.splice(from, 1);
      next.splice(i, 0, moved);
      return next;
    });
  }

  async function save() {
    if (!name.trim()) {
      toast.error("Outfits need a name. Try \"Sienna — Cannes look 1\".");
      return;
    }
    if (picks.length === 0) {
      toast.error("Add at least one piece before saving.");
      return;
    }
    setSaving(true);
    try {
      const created = await outfitsApi.create({
        name: name.trim(),
        description: description.trim() || undefined,
        client_id: clientId || undefined,
        occasion: (occasion || undefined) as Occasion | undefined,
        item_entries: picks.map((p) => ({ item_id: p.item._id, role: p.role })),
      });
      toast.success("Outfit saved.");
      navigate(`/app/outfits/${created._id}`);
    } catch (e: any) {
      toast.error(e?.message || "Could not save outfit.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <main className="flex-1 px-6 md:px-10 py-10" data-testid="outfit-new-page">
      <button
        type="button"
        onClick={() => navigate("/app/outfits")}
        data-testid="outfit-new-back"
        className="text-xs text-muted-foreground hover:text-oxblood inline-flex items-center gap-1 mb-4"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Back to outfits
      </button>

      <div className="grid lg:grid-cols-[1fr_360px] xl:grid-cols-[1fr_420px] gap-8">
        <section>
          <p className="text-[11px] uppercase tracking-[0.28em] text-oxblood font-medium mb-2">— Build outfit</p>
          <h1 className="font-display text-3xl sm:text-4xl">New lookbook</h1>

          <div className="mt-8 grid gap-4 max-w-xl">
            <div>
              <label className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">Outfit name</label>
              <Input data-testid="outfit-name-input" value={name} onChange={(e: any) => setName(e.target.value)}
                placeholder="Sienna — Cannes premiere look A" className="mt-1.5 h-11" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">Client</label>
                <select data-testid="outfit-client-select" className="mt-1.5 w-full h-11 border border-border bg-background px-3 text-sm"
                  value={clientId} onChange={(e) => setClientId(e.target.value)}>
                  <option value="">Unassigned</option>
                  {clients.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">Occasion</label>
                <select data-testid="outfit-occasion-select" className="mt-1.5 w-full h-11 border border-border bg-background px-3 text-sm"
                  value={occasion} onChange={(e) => setOccasion(e.target.value as Occasion)}>
                  <option value="">—</option>
                  {OCCASIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">Notes for the team</label>
              <Textarea data-testid="outfit-description-input" value={description} onChange={(e: any) => setDescription(e.target.value)}
                placeholder="Three options, prefer the silk for hero. Hair: low ponytail."
                className="mt-1.5 min-h-[90px]" />
            </div>
          </div>

          <div className="mt-12 border-t border-border pt-8">
            <h2 className="font-display text-xl mb-1">Pull from catalog</h2>
            <p className="text-xs text-muted-foreground mb-4">Click a piece to add — set a role on the right, drag to re-order.</p>
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input data-testid="outfit-pull-search" placeholder="Search items by name, brand, tag…"
                value={search} onChange={(e: any) => setSearch(e.target.value)} className="pl-9" />
            </div>
            <div data-testid="outfit-pull-grid"
              className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-3 max-h-[60vh] overflow-y-auto pr-1">
              {visible.length === 0 && <p className="col-span-full text-sm text-muted-foreground py-8 text-center">No items match.</p>}
              {visible.map((it) => (
                <button
                  type="button"
                  key={it._id}
                  data-testid={`pull-add-${it._id}`}
                  onClick={() => add(it)}
                  className="group text-left bg-background border border-border hover:border-oxblood/50 transition-colors"
                >
                  <div className="aspect-[4/5] bg-bone relative overflow-hidden">
                    {it.photos[0] ? (
                      <img src={fileUrl(it.photos[0].url)} alt={it.name} loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" />
                    ) : (
                      <div className="w-full h-full grid place-items-center text-[10px] uppercase tracking-[0.2em] text-muted-foreground">No photo</div>
                    )}
                    <span className="absolute top-2 right-2 w-7 h-7 grid place-items-center bg-oxblood text-ivory opacity-0 group-hover:opacity-100 transition-opacity">
                      <Plus className="w-3.5 h-3.5" />
                    </span>
                  </div>
                  <div className="p-2">
                    <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground truncate">{it.brand || it.category}</p>
                    <p className="text-xs leading-snug line-clamp-2 mt-0.5">{it.name}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>

        <aside data-testid="outfit-canvas" className="lg:sticky lg:top-24 self-start bg-bone/30 border border-border p-5">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="font-display text-lg">Your outfit</h2>
            <span className="text-xs text-muted-foreground">{picks.length} piece{picks.length === 1 ? "" : "s"}</span>
          </div>
          {picks.length === 0 ? (
            <div className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
              Empty rack. Click pieces from the catalog on the left to add them here.
            </div>
          ) : (
            <ul className="space-y-2">
              {picks.map((pk, i) => (
                <li
                  key={pk.item._id}
                  data-testid={`pick-${pk.item._id}`}
                  draggable
                  onDragStart={() => onDragStart(i)}
                  onDragOver={(e) => onDragOver(e, i)}
                  onDrop={() => onDrop(i)}
                  onDragEnd={() => { dragIndex.current = null; setDragOver(null); }}
                  className={`flex items-center gap-3 bg-background border ${dragOver === i ? "border-oxblood" : "border-border"} p-2 cursor-move`}
                >
                  <GripVertical className="w-4 h-4 text-muted-foreground shrink-0" />
                  <div className="w-12 h-12 bg-bone overflow-hidden shrink-0">
                    {pk.item.photos[0] && (
                      <img src={fileUrl(pk.item.photos[0].url)} alt={pk.item.name} loading="lazy"
                        className="w-full h-full object-cover" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground truncate">{pk.item.brand || pk.item.category}</p>
                    <p className="text-sm leading-snug line-clamp-1">{pk.item.name}</p>
                    <select
                      data-testid={`pick-role-${pk.item._id}`}
                      aria-label={`Role for ${pk.item.name}`}
                      value={pk.role}
                      onChange={(e) => setRole(pk.item._id, e.target.value as OutfitRole)}
                      // Cancel drag-start if the user is grabbing the role select.
                      onMouseDown={(e) => e.stopPropagation()}
                      className="mt-1 h-6 text-[11px] uppercase tracking-[0.16em] border border-border bg-background pl-1.5 pr-1 text-muted-foreground hover:text-foreground"
                    >
                      {OUTFIT_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                  <button type="button" onClick={() => remove(pk.item._id)}
                    data-testid={`pick-remove-${pk.item._id}`}
                    aria-label={`Remove ${pk.item.name}`}
                    className="text-muted-foreground hover:text-oxblood p-1 self-start">
                    <X className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <Button data-testid="outfit-save-btn" onClick={save} disabled={saving}
            className="mt-5 w-full bg-oxblood hover:bg-oxblood-deep text-ivory rounded-none h-11">
            {saving ? "Saving…" : "Save outfit"}
          </Button>
        </aside>
      </div>
    </main>
  );
}
